// import styles
import "./Dashboard.css";

// import componenti
import ProjectFilter from "./ProjectFilter";

// import hooks
import { useCollection } from "../../hooks/useCollection";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useDocument } from "../../hooks/useDocument";
import { useUserRole } from "../../hooks/useUserRole";

// import pacchetti
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFirestore } from "../../hooks/useFirestore";

export default function Dashboard() {
  // Ottieni l'utente corrente e il suo ruolo
  const { user } = useAuthContext();
  const { isAdmin, isResTecnico } = useUserRole();
  const { document: userDoc } = useDocument("users", user?.uid);
  const { updateDocument } = useFirestore("projects");

  // Rimuovo user perché non è più utilizzato dopo la rimozione del filtro 'miei'
  const { documents: projectDocs, error } = useCollection("projects");
  const { documents: settoriDocs } = useCollection("settori");
  const [activeFilter, setActiveFilter] = useState("tutti");
  const [searchTerm, setSearchTerm] = useState("");
  const [settoriOptions, setSettoriOptions] = useState([]);
  const [tipoProjectFilter, setTipoProjectFilter] = useState("tutti");
  const [completamentoFilter, setCompletamentoFilter] = useState("tutti");
  const [showArchived, setShowArchived] = useState(false);
  const [showCostCenters, setShowCostCenters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "nome", dir: "asc" });
  const navigazione = useNavigate();

  // Preparazione delle opzioni per il filtro dei settori
  useEffect(() => {
    if (settoriDocs) {
      setSettoriOptions(settoriDocs);
    }
  }, [settoriDocs]);

  const changeFilter = (newFilter) => {
    setActiveFilter(newFilter);
  };

  // Gestisce il termine di ricerca dal componente ProjectFilter
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Gestisce il cambio del filtro per tipoProject
  const handleTipoProjectFilter = (newFilter) => {
    setTipoProjectFilter(newFilter);
  };

  // Gestisce il cambio del filtro per completamento
  const handleCompletamentoFilter = (newFilter) => {
    setCompletamentoFilter(newFilter);
  };

  const filtredDoc = projectDocs
    ? projectDocs.filter((doc) => {
        // Se l'utente è un resTecnico e non è admin, filtra per settore
        if (isResTecnico && !isAdmin && userDoc?.anagrafica?.settore) {
          // Se il settore del progetto non corrisponde a quello dell'utente, non mostrare
          if (doc.categoria !== userDoc.anagrafica.settore) {
            return false;
          }
        }

        // Escludi centri di costo se il toggle non è attivo
        if (!showCostCenters && doc.tipoProject === "centro-di-costo") {
          return false;
        }

        // Filtraggio per completamento
        let matchesCompletamento = false;
        switch (completamentoFilter) {
          case "tutti":
            matchesCompletamento = true;
            break;
          case "completato":
            matchesCompletamento = doc.completato === true;
            break;
          case "in-corso":
            matchesCompletamento = doc.completato !== true;
            break;
          default:
            matchesCompletamento = true;
        }

        // Filtraggio per categoria/stato (senza completati e scaduti)
        let matchesFilter = false;
        switch (activeFilter) {
          case "tutti":
            matchesFilter = true;
            break;
          default:
            // Per i filtri di settore
            matchesFilter = doc.categoria === activeFilter;
        }

        // Filtraggio per tipoProject
        let matchesTipoProject =
          tipoProjectFilter === "tutti" ||
          doc.tipoProject === tipoProjectFilter;

        // Filtraggio per termine di ricerca
        const matchesSearch =
          searchTerm === "" ||
          doc.nome.toLowerCase().includes(searchTerm) ||
          doc.cliente?.toLowerCase().includes(searchTerm) ||
          doc.localizzazione?.toLowerCase().includes(searchTerm) ||
          doc.categoria?.toLowerCase().includes(searchTerm) ||
          doc.codice?.toLowerCase().includes(searchTerm);

        // Deve corrispondere a tutti i filtri
        return (
          matchesCompletamento &&
          matchesFilter &&
          matchesSearch &&
          matchesTipoProject
        );
      })
    : null;

  const sortedDocs = useMemo(() => {
    if (!filtredDoc) return null;
    const base = showArchived
      ? [...filtredDoc]
      : filtredDoc.filter((d) => !d.archived);

    const collator = new Intl.Collator("it", {
      sensitivity: "base",
      numeric: true,
    });
    const getValue = (p, key) => {
      switch (key) {
        case "nome":
          return (p.nome || "").toLowerCase();
        case "categoria":
          return (p.categoria || "").toLowerCase();
        case "tipoProject":
          return (p.tipoProject || "").toLowerCase();
        case "cliente":
          return p.tipoProject === "centro-di-costo"
            ? ""
            : (p.cliente || "").toLowerCase();
        case "codice":
          return (p.codice || "").toLowerCase();
        case "stato":
          return p.completato ? 1 : 0; // 1=Completato, 0=In corso
        case "archived":
          return p.archived ? 1 : 0;
        default:
          return (p.nome || "").toLowerCase();
      }
    };

    return base.sort((a, b) => {
      // Gestione priorità archiviati: se l'ordinamento è per 'archived', rispetta la direzione scelta,
      // altrimenti mostra sempre prima i non archiviati.
      const aArch = a.archived ? 1 : 0;
      const bArch = b.archived ? 1 : 0;
      if (sortConfig.key === "archived") {
        if (aArch !== bArch) {
          return sortConfig.dir === "asc" ? aArch - bArch : bArch - aArch;
        }
      } else {
        if (aArch !== bArch) {
          return aArch - bArch;
        }
      }

      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);
      let cmp = 0;

      if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal;
      } else {
        cmp = collator.compare(String(aVal), String(bVal));
      }

      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
  }, [filtredDoc, showArchived, sortConfig]);

  const toggleArchived = async (proj) => {
    const next = !Boolean(proj.archived);
    await updateDocument(proj.id, { archived: next });
  };

  const handleHeaderSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  return (
    <div>
      <div className="title-container">
        <h2 className="page-title">Projects</h2>
        <button onClick={() => navigazione("/create")} className="btn">
          Crea nuovo Project
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {projectDocs && (
        <ProjectFilter
          filtro={activeFilter}
          change={changeFilter}
          onSearch={handleSearch}
          settori={settoriOptions}
          tipoProjectFilter={tipoProjectFilter}
          onTipoProjectChange={handleTipoProjectFilter}
          completamentoFilter={completamentoFilter}
          onCompletamentoChange={handleCompletamentoFilter}
          showArchived={showArchived}
          onToggleArchived={setShowArchived}
          showCostCenters={showCostCenters}
          onToggleCostCenters={setShowCostCenters}
        />
      )}
      {sortedDocs && (
        <div className="project-table-wrapper">
          <table className="project-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("nome")}
                >
                  Nome{" "}
                  {sortConfig.key === "nome"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("categoria")}
                >
                  Categoria{" "}
                  {sortConfig.key === "categoria"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("tipoProject")}
                >
                  Tipo{" "}
                  {sortConfig.key === "tipoProject"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("cliente")}
                >
                  Cliente{" "}
                  {sortConfig.key === "cliente"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("codice")}
                >
                  Codice{" "}
                  {sortConfig.key === "codice"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("stato")}
                >
                  Stato{" "}
                  {sortConfig.key === "stato"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("archived")}
                >
                  Archivio{" "}
                  {sortConfig.key === "archived"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((p) => (
                <tr
                  key={p.id}
                  className={
                    p.archived ? "archived-row clickable-row" : "clickable-row"
                  }
                  onClick={() => navigazione(`/project/${p.id}`)}
                >
                  <td>{p.nome}</td>
                  <td>{p.categoria}</td>
                  <td>{p.tipoProject || "-"}</td>
                  <td>
                    {p.tipoProject !== "centro-di-costo"
                      ? p.cliente || "-"
                      : "-"}
                  </td>
                  <td>{p.codice || "-"}</td>
                  <td>{p.completato ? "Completato" : "In corso"}</td>
                  <td>
                    {p.archived ? (
                      <span className="badge badge-archived">Archiviato</span>
                    ) : (
                      <span className="badge badge-active">Attivo</span>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      {(isAdmin || isResTecnico) && (
                        <button
                          className="btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleArchived(p);
                          }}
                          title={p.archived ? "Ripristina" : "Archivia"}
                        >
                          {p.archived ? "Ripristina" : "Archivia"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedDocs.length === 0 && (
            <p className="no-data">Nessun project trovato</p>
          )}
        </div>
      )}
    </div>
  );
}
